import { pipeline } from '@xenova/transformers'
import db from './db'
import { stripHtml } from '../utils/textUtils'

// Singleton state
let embeddingPipeline = null
let isModelLoading = false
let modelReady = false

// Initialize the embedding model (loads ~23MB ONNX model, cached after first download)
export async function initEmbeddingModel(onProgress = null) {
  if (embeddingPipeline) return embeddingPipeline
  if (isModelLoading) {
    // Wait for existing load to finish
    while (isModelLoading) {
      await new Promise(r => setTimeout(r, 100))
    }
    return embeddingPipeline
  }

  isModelLoading = true
  try {
    embeddingPipeline = await pipeline(
      'feature-extraction',
      'Xenova/all-MiniLM-L6-v2',
      { progress_callback: onProgress }
    )
    modelReady = true
    return embeddingPipeline
  } catch (error) {
    console.error('Failed to load embedding model:', error)
    embeddingPipeline = null
    modelReady = false
    throw error
  } finally {
    isModelLoading = false
  }
}

// Check if model is loaded and ready
export function isModelReady() {
  return modelReady
}

// Generate embedding vector for a string of text
// Returns array of 384 floats
export async function generateEmbedding(text) {
  if (!embeddingPipeline) return null
  if (!text || text.trim().length === 0) return null

  // Truncate to ~256 tokens worth of text to stay within MiniLM's window
  const truncated = text.slice(0, 1200)

  const output = await embeddingPipeline(truncated, {
    pooling: 'mean',
    normalize: true,
  })

  return Array.from(output.data)
}

// Embed a single note and store in IndexedDB
export async function embedNote(note) {
  const plainText = stripHtml(note.content || '')
  const textToEmbed = `${note.title || ''} ${plainText}`
  const vector = await generateEmbedding(textToEmbed)

  if (!vector) return false

  await db.embeddings.put({ noteId: note.id, vector })
  return true
}

// Batch-embed multiple notes with progress callback
export async function embedNotes(notes, onProgress = null) {
  let embedded = 0
  for (let i = 0; i < notes.length; i++) {
    try {
      await embedNote(notes[i])
      embedded++
    } catch (err) {
      console.error(`Failed to embed note ${notes[i].id}:`, err)
    }
    if (onProgress) onProgress(i + 1, notes.length)
  }
  return embedded
}

// Get stored embedding for a note
export async function getNoteEmbedding(noteId) {
  const record = await db.embeddings.get(noteId)
  return record ? record.vector : null
}

// Get all stored embeddings
export async function getAllEmbeddings() {
  return await db.embeddings.toArray()
}

// Embed any notes that don't have embeddings yet
export async function embedMissingNotes(allNotes, onProgress = null) {
  const existingIds = new Set(
    (await db.embeddings.toArray()).map(e => e.noteId)
  )
  const missing = allNotes.filter(n => !existingIds.has(n.id))

  if (missing.length === 0) return 0
  return await embedNotes(missing, onProgress)
}

// Delete embedding when a note is deleted
export async function deleteNoteEmbedding(noteId) {
  await db.embeddings.where('noteId').equals(noteId).delete()
  return true
}

export default {
  initEmbeddingModel,
  isModelReady,
  generateEmbedding,
  embedNote,
  embedNotes,
  getNoteEmbedding,
  getAllEmbeddings,
  embedMissingNotes,
  deleteNoteEmbedding,
}
